import React, { useState, useEffect } from 'react';
import { X, Shield, Copy, CheckCircle, Clock, RefreshCw, AlertCircle, ExternalLink, Trash2 } from 'lucide-react';
import {
  setupDomainAuthentication,
  verifyDomainAuthentication,
  getDomainStatus,
  resetDomainAuthentication,
  formatDnsRecords,
  copyToClipboard,
  type DomainAuthStatus,
} from '../lib/sendgridHelper';

interface AdminDomainAuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  userEmail: string;
  userName: string;
  userCompany: string;
  onUpdate?: () => void;
}

export function AdminDomainAuthModal({
  isOpen,
  onClose,
  userId,
  userEmail,
  userName,
  userCompany,
  onUpdate,
}: AdminDomainAuthModalProps) {
  const [domainStatus, setDomainStatus] = useState<DomainAuthStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [settingUp, setSettingUp] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const [domainInput, setDomainInput] = useState('');
  const [subdomainInput, setSubdomainInput] = useState('');

  useEffect(() => {
    if (isOpen) {
      loadDomainStatus();
    }
  }, [isOpen, userId]);

  const loadDomainStatus = async () => {
    try {
      setLoading(true);
      setError('');
      console.log('[AdminDomainAuthModal] Loading domain status for userId:', userId);
      const status = await getDomainStatus(userId);
      console.log('[AdminDomainAuthModal] Domain status received:', status);
      setDomainStatus(status);
    } catch (err) {
      console.error('[AdminDomainAuthModal] Error loading domain status:', err);
      setError(`Failed to load domain status: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSetupDomain = async () => {
    if (!domainInput) {
      setError('Domain is vereist');
      return;
    }

    try {
      setSettingUp(true);
      setError('');
      setSuccess('');

      console.log('[AdminDomainAuthModal] Setting up domain:', { userId, domain: domainInput, subdomain: subdomainInput });

      const result = await setupDomainAuthentication(
        userId,
        domainInput,
        subdomainInput || undefined
      );

      console.log('[AdminDomainAuthModal] Setup result:', result);

      if (result.success) {
        setSuccess('Domain authentication succesvol opgezet!');
        setDomainInput('');
        setSubdomainInput('');
        await loadDomainStatus();
        if (onUpdate) onUpdate();
      } else {
        console.error('[AdminDomainAuthModal] Setup failed:', result.error);
        setError(result.error || 'Failed to setup domain');
      }
    } catch (err: any) {
      console.error('[AdminDomainAuthModal] Setup exception:', err);
      setError(err.message || 'An error occurred');
    } finally {
      setSettingUp(false);
    }
  };

  const handleVerifyDomain = async () => {
    try {
      setVerifying(true);
      setError('');
      setSuccess('');

      const result = await verifyDomainAuthentication(userId);

      if (result.success) {
        if (result.verified) {
          setSuccess('Domain succesvol geverifieerd!');

          // Update local state optimistically with verified status from the verify response
          if (domainStatus && result.validationResults) {
            setDomainStatus({
              ...domainStatus,
              verified: true,
              dnsRecords: domainStatus.dnsRecords ? {
                mail_cname: { ...domainStatus.dnsRecords.mail_cname, valid: result.validationResults.mailCname },
                dkim1: { ...domainStatus.dnsRecords.dkim1, valid: result.validationResults.dkim1 },
                dkim2: { ...domainStatus.dnsRecords.dkim2, valid: result.validationResults.dkim2 },
              } : undefined,
              validationStatus: result.validationResults,
            });
          }

          // Reload status after a delay to allow SendGrid API to propagate
          setTimeout(() => {
            loadDomainStatus();
          }, 3000);
        } else {
          const failedRecords = result.failedRecords?.map(r => r.type).join(', ') || 'DNS records';
          setError(`Verificatie mislukt. Controleer: ${failedRecords}`);

          // Update with failed validation results
          if (domainStatus && result.validationResults) {
            setDomainStatus({
              ...domainStatus,
              verified: false,
              dnsRecords: domainStatus.dnsRecords ? {
                mail_cname: { ...domainStatus.dnsRecords.mail_cname, valid: result.validationResults.mailCname },
                dkim1: { ...domainStatus.dnsRecords.dkim1, valid: result.validationResults.dkim1 },
                dkim2: { ...domainStatus.dnsRecords.dkim2, valid: result.validationResults.dkim2 },
              } : undefined,
              validationStatus: result.validationResults,
            });
          }
        }
        if (onUpdate) onUpdate();
      } else {
        setError(result.error || 'Verificatie mislukt');
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred');
    } finally {
      setVerifying(false);
    }
  };

  const handleResetDomain = async () => {
    if (!confirm('Weet je zeker dat je domain authentication wilt resetten voor deze user?')) {
      return;
    }

    try {
      setResetting(true);
      setError('');
      setSuccess('');

      const result = await resetDomainAuthentication(userId);

      if (result) {
        setSuccess('Domain authentication gereset');
        setDomainStatus(null);
        if (onUpdate) onUpdate();
      } else {
        setError('Failed to reset domain authentication');
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred');
    } finally {
      setResetting(false);
    }
  };

  const handleCopyToClipboard = async (text: string, field: string) => {
    const success = await copyToClipboard(text);
    if (success) {
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-[#231F20] flex items-center gap-2">
              <Shield className="w-6 h-6 text-[#FF6B35]" />
              Domain Authentication Beheer
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {userName} ({userEmail}) - {userCompany}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-red-800 font-medium mb-1">Error</p>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          )}

          {success && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-green-800">{success}</p>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin text-[#FF6B35]" />
            </div>
          ) : domainStatus?.configured && domainStatus.domain ? (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-sm font-medium text-gray-700">Geconfigureerd Domein</p>
                    <p className="text-lg font-semibold text-[#231F20] mt-1">{domainStatus.domain}</p>
                    {domainStatus.subdomain && (
                      <p className="text-sm text-gray-600">Subdomain: {domainStatus.subdomain}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {domainStatus.verified ? (
                      <div className="flex items-center gap-2 px-3 py-1 bg-green-100 text-green-800 rounded-full">
                        <CheckCircle className="w-4 h-4" />
                        <span className="text-sm font-medium">Geverifieerd</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full">
                        <Clock className="w-4 h-4" />
                        <span className="text-sm font-medium">Wacht op DNS</span>
                      </div>
                    )}
                  </div>
                </div>

              </div>

              {domainStatus.dnsRecords && domainStatus.dnsRecords.mail_cname && domainStatus.dnsRecords.dkim1 && domainStatus.dnsRecords.dkim2 && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-medium text-gray-700">DNS Records</h3>
                    <div className="flex items-center gap-2">
                      {!domainStatus.verified && (
                        <button
                          onClick={handleVerifyDomain}
                          disabled={verifying}
                          className="text-sm text-[#FF6B35] hover:text-[#FF6B35]/80 font-medium flex items-center gap-1"
                        >
                          <RefreshCw className={`w-4 h-4 ${verifying ? 'animate-spin' : ''}`} />
                          {verifying ? 'Verificatie...' : 'Verifieer DNS'}
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left font-medium text-gray-700 w-32">Record</th>
                          <th className="px-4 py-2 text-left font-medium text-gray-700 w-24">Type</th>
                          <th className="px-4 py-2 text-left font-medium text-gray-700 w-80">Host</th>
                          <th className="px-4 py-2 text-left font-medium text-gray-700">Waarde</th>
                          <th className="px-4 py-2 text-center font-medium text-gray-700 w-20">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {formatDnsRecords(domainStatus.dnsRecords).map((record, index) => (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-4 py-3 font-medium text-gray-900">{record.name}</td>
                            <td className="px-4 py-3">
                              <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-mono">
                                {record.type}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <code className="text-xs bg-gray-100 px-2 py-1 rounded break-all font-mono">
                                  {record.host}
                                </code>
                                <button
                                  onClick={() => handleCopyToClipboard(record.host, `host-${index}`)}
                                  className="text-gray-400 hover:text-gray-600 flex-shrink-0"
                                >
                                  {copiedField === `host-${index}` ? (
                                    <CheckCircle className="w-4 h-4 text-green-600" />
                                  ) : (
                                    <Copy className="w-4 h-4" />
                                  )}
                                </button>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <code className="text-xs bg-gray-100 px-2 py-1 rounded break-all font-mono">
                                  {record.value}
                                </code>
                                <button
                                  onClick={() => handleCopyToClipboard(record.value, `value-${index}`)}
                                  className="text-gray-400 hover:text-gray-600 flex-shrink-0"
                                >
                                  {copiedField === `value-${index}` ? (
                                    <CheckCircle className="w-4 h-4 text-green-600" />
                                  ) : (
                                    <Copy className="w-4 h-4" />
                                  )}
                                </button>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex justify-center">
                                {record.valid ? (
                                  <div className="flex items-center gap-1 px-2 py-1 bg-green-50 text-green-700 rounded">
                                    <CheckCircle className="w-4 h-4" />
                                    <span className="text-xs font-medium">OK</span>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-1 px-2 py-1 bg-yellow-50 text-yellow-700 rounded">
                                    <Clock className="w-4 h-4" />
                                    <span className="text-xs font-medium">Wacht</span>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                </div>
              )}

              <div className="flex justify-between pt-4 border-t border-gray-200">
                <button
                  onClick={handleResetDomain}
                  disabled={resetting}
                  className="px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg flex items-center gap-2 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  {resetting ? 'Resetten...' : 'Reset Domain Auth'}
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-900">
                  Geen domain authentication geconfigureerd voor deze user. Configureer hieronder.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Domein <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={domainInput}
                  onChange={(e) => setDomainInput(e.target.value)}
                  placeholder="bijvoorbeeld.nl"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B35] focus:border-transparent outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subdomain (optioneel)
                </label>
                <input
                  type="text"
                  value={subdomainInput}
                  onChange={(e) => setSubdomainInput(e.target.value)}
                  placeholder="mail"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B35] focus:border-transparent outline-none"
                />
              </div>

              <button
                onClick={handleSetupDomain}
                disabled={settingUp || !domainInput}
                className="btn-primary inline-flex items-center gap-2"
              >
                <Shield className="w-5 h-5" />
                {settingUp ? 'Instellen...' : 'Setup Domain Authentication'}
              </button>
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end">
          <button onClick={onClose} className="btn-secondary">
            Sluiten
          </button>
        </div>
      </div>
    </div>
  );
}
